<template>
  <div>
    <ion-toolbar>
      <ion-buttons
        slot="start"
        v-if="
          $store.state.pages.auth.back.length && routeName == 'auth_business'
        "
      >
        <ion-button class="button_back" @click="goBack">
          <ion-icon name="arrow-back-outline"></ion-icon>
        </ion-button>
      </ion-buttons>
      <ion-buttons slot="end">
        <ion-button
          v-if="routeName == 'auth_business'"
          class="pill next-pill-button"
          @click="skip"
        >
          <span>Skip</span>
        </ion-button>
      </ion-buttons>
    </ion-toolbar>

    <div class="main_wrapper ion-padding">
      <div class="container">
        <page-title
          title="Business Details"
          subtitle="Please enter details!"
          showIcon
        />
        <el-form
          class="full_page"
          :model="formsData.userForm"
          :rules="formsData.userForm.rules"
          :ref="(el) => (formsData.userForm.ref = el)"
          :label-position="'top'"
          @submit.prevent="goNext('userForm')"
          @keyup.enter="goNext('userForm')"
        >
          <el-form-item class="no-scroll" prop="businessName" :required="true">
            <el-input
              v-model="formsData.userForm.businessName.value"
              :placeholder="'Business Name'"
              @input="checkFormValidation('userForm', false)"
              @blur="checkFormValidation('userForm', true, 'businessName')"
            ></el-input>
          </el-form-item>
          <el-form-item class="no-scroll" prop="abn" :required="true">
            <div class="el-input">
              <input
                type="tel"
                class="el-input__inner"
                :ref="(el) => (formsData.userForm.abn.ref = el)"
                :value="formsData.userForm.abn.value"
                placeholder="ABN"
                v-imask="formsData.userForm.abn.mask"
                @accept="onFormMaskAccept($event, 'userForm', 'abn')"
                @blur="checkFormValidation('userForm', true, 'abn')"
              />
            </div>
          </el-form-item>
          <el-form-item prop="billing_type" :required="true">
            <el-select
              class="covid-cert-select"
              popper-class="normal-select"
              style="width: 100%"
              :ref="(el) => (formsData.userForm.billing_type.ref = el)"
              v-model="formsData.userForm.billing_type.value"
              @change="checkFormValidation('userForm', true, 'billingType')"
              placeholder="Select Entity Type"
            >
              <el-option label="Company / Trust / Partnership" :value="1" />
              <el-option label="Sole Trader" :value="2" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button
              class="submit"
              type="blue"
              @click="goNext('userForm')"
              :disabled="!formsData.userForm.isFormValid"
            >
              Continue
            </el-button>
          </el-form-item>

          <div class="text-center form-option-link">
            Don’t have an ABN?
            <a
              href="https://www.abr.gov.au/business-super-funds-charities/applying-abn"
              target="_blank"
              class="redirect-link"
              >Sign Up here</a
            >
          </div>
        </el-form>
      </div>
    </div>

    <div class="background-circles">
      <div class="circle circle-2"></div>
      <div class="circle circle-1"></div>
      <SvgIcon
        name="bgIcon"
        class="bg-icon"
        width="174"
        height="174"
        viewBox="0 0 174 174"
        fill="none"
      />
    </div>
  </div>
</template>

<script>
import "./Business.scss";
import { onMounted, inject } from "vue";
import { addIcons } from "ionicons";
import { arrowBackOutline } from "ionicons/icons";
import { IonToolbar, IonButton, IonButtons, IonIcon } from "@ionic/vue";
import { useStore } from "vuex";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import formHooks from "@/hooks/form/form";
import formValidate from "@/hooks/form/validate/validate";
import formMasks from "@/hooks/form/mask/mask";
import IMaskDirective from "@/directives/vue-imask/directive";
import IMask from "imask/esm/imask";
import { ElMessageBox } from "@/components/Vendor/Element";
import PageTitle from "@/components/PageTitle/PageTitle.vue";
import SvgIcon from "@/components/Svg/SvgIcon.vue";
addIcons({
  "arrow-back-outline": arrowBackOutline,
});

export default {
  components: {
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    PageTitle,
    SvgIcon,
  },
  directives: {
    imask: IMaskDirective,
  },
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const $route = useRoute();
    const setPageClassGlobal = inject("setPageClassGlobal");

    const userData = $store.getters.GET_USER_DATA;
    const routeName = $route.name;

    const formsDataArray = {
      userForm: {
        isFormValid: true,
        businessName: {
          value: userData.businessNameBilling
            ? userData.businessNameBilling
            : "",
          resetValue: true,
          validators: [
            {
              validator: (rule, formField, callback) => {
                formValidate.validate(
                  rule,
                  formField,
                  "businessName",
                  callback,
                  setFormValidation
                );
              },
            },
          ],
        },
        abn: {
          value: userData.abn
            ? IMask.createMask(formMasks.abn).resolve(userData.abn)
            : "",
          mask: formMasks.abn,
          resetValue: true,
          validators: [
            {
              validator: (rule, formField, callback) => {
                formValidate.validate(
                  rule,
                  formField,
                  "abn",
                  callback,
                  setFormValidation
                );
              },
            },
          ],
        },
        billing_type: {
          value: userData.billingAccountType ? userData.billingAccountType : "",
        },
      },
    };

    const goBack = async () => {
      if (routeName == "profile_business") {
        $router.push({
          name: "profile",
        });
      } else {
        const back = await $store.dispatch("getAuthPageBack");
        $router.push({
          name: back,
        });
      }
    };

    const goNext = (formName) => {
      formsRefs[formName].value.validate((valid) => {
        if (valid) {
          (async () => {
            try {
              if (routeName == "profile_business") {
                checkFormChanged("userForm", true);
                await $store.dispatch(
                  "SAVE_USER_DATA",
                  getFormValues(formName)
                );
                copyFormResetValue("userForm", true);
                $router.push({
                  name: "profile",
                });
              } else {
                await $store.dispatch(
                  "SAVE_USER_DATA",
                  getFormValues(formName)
                );
                $router.push({
                  name: "auth_bank",
                });
              }
            } catch (error) {
              console.log("error submit!!");
            }
          })();
        } else {
          setFormValidation(formName, false, false);
          return false;
        }
      });
    };

    const skip = () => {
      (async () => {
        try {
          await ElMessageBox.confirm(
            ``,
            "Do you want to skip? You will be unverified",
            {
              distinguishCancelAndClose: true,
              showConfirmButton: true,
              confirmButtonText: "Skip",
              cancelButtonText: "Cancel",
              confirmButtonClass: "el-button--green",
              cancelButtonClass: "el-button--grey",
              showClose: true,
            }
          );
        } catch {
          return;
        }
        try {
          await $store.dispatch("addAuthPageBack", $route.name);
          $router.push({
            name: "auth_bank",
          });
        } catch (error) {
          console.log(error);
        }
      })();
    };

    onBeforeRouteLeave((to, from, next) => {
      if (routeName == "profile_business") {
        (async () => {
          try {
            let leave = await checkFormChanged("userForm");
            if (leave == "save") {
              await goNext("userForm");
            } else if (!leave) {
              next(false);
            } else {
              next();
            }
          } catch (error) {
            next(false);
            console.log(error);
          }
        })();
      } else {
        next();
      }
    });

    onMounted(() => {
      setPageClassGlobal("global_auth_page");
      setFormRefs();
      formsRefs.userForm.value.$el[0].focus();
      if (routeName == "profile_business") {
        checkFormValidation("userForm", true);
      } else {
        checkFormValidation("userForm", false);
      }
    });

    const {
      ["onFormMaskAccept"]: onFormMaskAccept,
      checkFormValidation,
      setFormValidation,
      copyFormResetValue,
      checkFormChanged,
      getFormValues,
      setFormRefs,
      formsData,
      formsRefs,
    } = formHooks(formsDataArray);

    return {
      formsData,
      onFormMaskAccept,
      checkFormValidation,
      goBack,
      goNext,
      skip,
      routeName,
    };
  },
};
</script>
